import { useState } from "react";
import PhotoSizeHint from "../../components/documents/PhotoSizeHint";
import SellerStep from "../../components/seller/SellerStep";
import { SharedButton, SharedFormField } from "../../components/shared";
import { addClientDocument } from "../../js/db/documents";
import { useSellerState } from "../../js/state/sellerState";

export default function SellerPhotos() {
  const { seller } = useSellerState();
  const saved = seller.portfolio.photos || {};
  const [form, setForm] = useState({
    items: saved.items || [],
    photographer: saved.photographer || "",
    notes: saved.notes || "",
  });
  const [error, setError] = useState("");

  async function addFiles(event) {
    const files = Array.from(event.target.files || []);
    event.target.value = "";
    setError("");
    const additions = [];
    for (const file of files) {
      try {
        const ownerId = seller.id || seller.email || "seller-local";
        const doc = await addClientDocument({
          ownerId,
          role: "seller",
          file,
          kind: "photo",
        });
        additions.push({
          id: doc.id,
          fileName: doc.file_name,
          file_path: doc.file_path,
          mime_type: doc.mime_type,
          room: "",
          caption: "",
          addedAt: doc.created_at,
        });
      } catch (err) {
        setError(err.message);
      }
    }
    if (additions.length) {
      setForm((current) => ({ ...current, items: [...current.items, ...additions] }));
    }
  }

  function updateItem(id, event) {
    const { name, value } = event.target;
    setForm((current) => ({
      ...current,
      items: current.items.map((item) => (item.id === id ? { ...item, [name]: value } : item)),
    }));
  }

  return (
    <SellerStep
      title="Photos"
      lead="Add listing photos to this account. They appear in the agent/broker Documents & Photos vault and on the File Packet."
      sectionKey="photos"
      value={form}
    >
      <section className="checklist-section">
        <h2>Photo Inventory</h2>
        <label className="shared-field field">
          <span className="field-label">Add photos from your device</span>
          <input className="field-input" type="file" accept="image/*" multiple onChange={addFiles} />
          <PhotoSizeHint />
          {error ? <span className="field-error">{error}</span> : null}
        </label>
        <SharedButton
          variant="secondary"
          onClick={() =>
            setForm((current) => ({
              ...current,
              items: [
                ...current.items,
                { id: crypto.randomUUID(), fileName: "", room: "", caption: "", addedAt: new Date().toISOString() },
              ],
            }))
          }
        >
          Add a photo note
        </SharedButton>

        <div className="seller-photo-list">
          {form.items.length === 0 ? (
            <p className="field-hint">No photos listed yet. Add files or a note for each room you want shot.</p>
          ) : (
            form.items.map((item) => (
              <div key={item.id} className="seller-repeat-row">
                <div className="two-col">
                  <SharedFormField
                    label="File name"
                    name="fileName"
                    value={item.fileName}
                    onChange={(event) => updateItem(item.id, event)}
                    placeholder="kitchen-1.jpg"
                  />
                  <SharedFormField
                    label="Room / area"
                    name="room"
                    value={item.room}
                    onChange={(event) => updateItem(item.id, event)}
                    placeholder="Kitchen, front elevation…"
                  />
                </div>
                <SharedFormField
                  label="Caption / shot notes"
                  name="caption"
                  value={item.caption}
                  onChange={(event) => updateItem(item.id, event)}
                />
                <SharedButton
                  variant="ghost"
                  onClick={() =>
                    setForm((current) => ({
                      ...current,
                      items: current.items.filter((entry) => entry.id !== item.id),
                    }))
                  }
                >
                  Remove
                </SharedButton>
              </div>
            ))
          )}
        </div>
        <SharedFormField
          label="Photographer / media plan"
          name="photographer"
          value={form.photographer}
          onChange={(event) => setForm((current) => ({ ...current, photographer: event.target.value }))}
          placeholder="DIY, listing photographer booked, drone, video tour…"
        />
        <SharedFormField
          label="Photo notes for my agent"
          name="notes"
          type="textarea"
          value={form.notes}
          onChange={(event) => setForm((current) => ({ ...current, notes: event.target.value }))}
          placeholder="Rooms to hide, pets, staging timeline, twilight shot, HOA photo rules."
        />
      </section>
    </SellerStep>
  );
}
